import * as React from 'react';

import Alert from '@mui/material/Alert';

import { getDailyReportPreviewEnd } from 'services/clients/backupBank';

import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { useRegion } from 'common/hooks/region';
import { objectKeysToSnakeCase } from 'utils/common';

export interface SpecificDateSearch {
  date: string;
  time: string;
  shippingLine: string;
  containerSize: string;
}

interface Props {
  search: SpecificDateSearch;
}

interface LoadingState {
  status: 'loading';
}

interface SuccessState {
  status: 'success';
  previewImageUrl: string | null;
}

interface FailureState {
  status: 'failure';
  error: unknown;
}

type State = LoadingState | SuccessState | FailureState;

export const Preview: React.FC<Props> = (props: Props) => {
  const region = useRegion();
  const { search } = props;

  const [state, setState] = React.useState<State>({ status: 'loading' });

  React.useEffect(() => {
    const loadPreview = async () => {
      setState({ status: 'loading' });
      try {
        const response = await getDailyReportPreviewEnd({
          ...objectKeysToSnakeCase(search),
          port_collection_code: region.code,
        });
        const previewImageUrl = response.ea_table;
        setState({ status: 'success', previewImageUrl });
      } catch (error) {
        setState({ status: 'failure', error });
      }
    };
    loadPreview();
  }, [search, setState, region.code]);

  let content: JSX.Element | null = null;
  switch (state.status) {
    case 'loading':
      content = (
        <div className="h-full w-full flex justify-center py-12">
          <LoadingSpinner />
        </div>
      );
      break;
    case 'failure':
      content = (
        <div>
          <Alert severity="warning">
            Preview currently unavailable, please download full report.
          </Alert>
        </div>
      );
      break;
    case 'success': {
      content = state.previewImageUrl ? (
        <img src={state.previewImageUrl} alt="Empty Dispatch Table Preview" />
      ) : (
        <div className="h-full w-full py-12 px-4 text-center">
          <h3 className="font-medium text-gray-600">
            No preview available for your search. Please make another selection.
          </h3>
        </div>
      );
      break;
    }
    default:
      break;
  }

  return (
    <div className="w-full flex flex-col space-y-2 mb-8">
      <h4 className="text-dark font-medium">Empty Dispatch Table Preview</h4>
      <div className="w-full bg-white shadow-md">{content}</div>
    </div>
  );
};
Preview.displayName = 'SpecificDate.Preview';
